import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { mediaQuery } from '@/config/breakpoints';
import { useMediaQuery } from '@/lib/useMediaQuery';
import {
  applyThemeAttribute,
  readThemePref,
  resolveTheme,
  writeThemePref,
  type Theme,
  type ThemePref,
} from './theme';

interface ThemeContextValue {
  pref: ThemePref;
  theme: Theme;
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Тема: data-theme на <html>, dark / light / system, выбор сохраняется (NAV-4). */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => readThemePref());
  const systemPrefersLight = useMediaQuery(mediaQuery.prefersLight);
  const theme = resolveTheme(pref, systemPrefersLight);

  useEffect(() => {
    applyThemeAttribute(theme);
  }, [theme]);

  const setPref = useCallback((next: ThemePref) => {
    writeThemePref(next);
    setPrefState(next);
  }, []);

  const value = useMemo(() => ({ pref, theme, setPref }), [pref, theme, setPref]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}
